import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, C, setToken } from "../lib/api";

const AuthCtx = createContext(null);
const GUEST_KEY = "pfp_guest_token"; // this device's guest: "Play as guest" brings the same one back

// guests play on the house look: default felt and card faces
const guestDefaults = (u) => { if (u?.guest) { localStorage.setItem("pfp_felt", "table_felt_green"); localStorage.setItem("pfp_deck", "simple"); } };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, object=authed
  const [region, setRegion] = useState(null);
  const [offline, setOffline] = useState(false); // the room can't be reached (API down / network), not "signed out"

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(C.ApiRoutes.AUTH_ME);
      guestDefaults(data);
      setUser(data);
      setOffline(false);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401 || code === 403) { setUser(false); setOffline(false); return; }
      // No response or a 5xx: the API is restarting or the connection dropped. Keep the session and keep trying, so a
      // player sitting at a table is not thrown out to the sign-in page (and loses the table they were on).
      setOffline(true);
      setTimeout(refresh, 3000);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post(C.ApiRoutes.AUTH_LOGIN, { email, password });
    setToken(data.access_token);
    setUser(data.user);
    setRegion(data.region);
    return data;
  };
  const register = async (email, password, display_name, dob, referral_code) => {
    const { data } = await api.post(C.ApiRoutes.AUTH_REGISTER, { email, password, display_name, dob, referral_code });
    setToken(data.access_token);
    setUser(data.user);
    setRegion(data.region);
    return data;
  };
  const guest = async (adult) => {
    const { data } = await api.post("/api/auth/guest", { adult, guest_token: localStorage.getItem(GUEST_KEY) || undefined });
    setToken(data.access_token);
    localStorage.setItem(GUEST_KEY, data.access_token);
    guestDefaults(data.user);
    setUser(data.user);
    setRegion(data.region);
    return data;
  };
  const join = async (body) => {
    const { data } = await api.post("/api/auth/join", body);
    setToken(data.access_token);
    localStorage.removeItem(GUEST_KEY); // a member now: they sign in with email and password
    setUser(data.user);
    return data;
  };
  const logout = async () => {
    try { await api.post(C.ApiRoutes.AUTH_LOGOUT); } catch { /* ignore */ }
    setToken(null);
    setUser(false);
  };

  return <AuthCtx.Provider value={{ user, region, offline, login, register, guest, join, logout, refresh }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
